import { gql } from '@apollo/client';
import { VARIANTS_PACK_FRAGMENT } from '@/contexts/items-context-queries';

export const TAG_PRINT_TEMPLATE_DETAIL_FRAGMENT = gql`
  fragment PrintTagTemplateDetailFields on print_tag_templates {
    display_name
    metadata
    name
    offline_printer_text_template
    preview_image_url_list
    uuid
  }
`;

export const PRINT_TAG_TEMPLATE_FIELDS = gql`
  fragment PrintTagTemplateFields on print_tag_templates {
    metadata
    uuid
  }
`;

export const TAG_PRINT_RESTAURANT_OVERRIDES_FRAGMENT = gql`
  fragment TagPrintBatchVariantPackFields on variants_packs {
    ...ItemsContext_VariantsPackFields
    restaurant_variant_pack_overrides(
      where: { restaurant_uuid: { _eq: $restaurantUUID } }
    ) {
      user_defined_metadata
      uuid
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
`;

export const TAG_PRINT_BATCH_LIST_FIELDS_FRAGMENT = gql`
  fragment PrintTagBatchListFields on print_tag_batches {
    created_at
    name
    print_tag_batch_variant_packs_aggregate {
      aggregate {
        max {
          updated_at
        }
        sum {
          quantity
        }
      }
    }
    requested_at
    uuid
  }
`;

export const TAG_PRINT_BATCH_FRAGMENT = gql`
  fragment PrintTagBatchFields on print_tag_batches {
    name
    print_tag_batch_variant_packs(order_by: { updated_at: desc }) {
      quantity
      uuid
      variant_pack {
        ...TagPrintBatchVariantPackFields
      }
    }
    print_tag_template {
      ...PrintTagTemplateFields
    }
    uuid
  }

  ${TAG_PRINT_RESTAURANT_OVERRIDES_FRAGMENT}
  ${PRINT_TAG_TEMPLATE_FIELDS}
`;

export const TAG_PRINT_BATCH_FULL_FIELDS_FRAGMENT = gql`
  fragment PrintTagBatchFullFields on print_tag_batches {
    ...PrintTagBatchFields
    ...PrintTagBatchListFields
  }

  ${TAG_PRINT_BATCH_FRAGMENT}
  ${TAG_PRINT_BATCH_LIST_FIELDS_FRAGMENT}
`;

export const TAG_PRINT_BATCH_LIST_QUERY = gql`
  query TagPrint_BatchList($restaurantUUID: uuid!, $supplierUUID: uuid!) {
    print_tag_batches(
      order_by: { created_at: desc }
      where: {
        print_tag_template: { supplier_uuid: { _eq: $supplierUUID } }
        restaurant_uuid: { _eq: $restaurantUUID }
      }
    ) {
      ...PrintTagBatchListFields
    }
  }

  ${TAG_PRINT_BATCH_LIST_FIELDS_FRAGMENT}
`;

export const TAG_PRINT_BATCH_TEMPLATE_QUERY = gql`
  query TagPrint_BatchTemplate($restaurantUUID: uuid!, $supplierUUID: uuid!) {
    print_tag_template_assignments(
      where: {
        _or: [
          { restaurant_uuid: { _eq: $restaurantUUID } }
          { restaurant_uuid: { _is_null: true } }
        ]
        supplier_uuid: { _eq: $supplierUUID }
      }
    ) {
      print_tag_template {
        ...PrintTagTemplateDetailFields
      }
      uuid
    }
  }

  ${TAG_PRINT_TEMPLATE_DETAIL_FRAGMENT}
`;

export const TAG_PRINT_BATCH_DETAIL_QUERY = gql`
  query TagPrint_BatchDetail(
    $locale: String!
    $printTagBatchUUID: uuid!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
  ) {
    print_tag_batches_by_pk(uuid: $printTagBatchUUID) {
      ...PrintTagBatchFields
    }
  }

  ${TAG_PRINT_BATCH_FRAGMENT}
`;

export const RENDER_TAG_PRINT_BATCH_QUERY = gql`
  query TagPrint_RenderBatch(
    $printTagBatchUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    renderPrintTagBatchTemplatesForRestaurant(
      order_by: CREATED_DESC
      print_tag_batch_id: $printTagBatchUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      rendered_template
      variant_pack_id
    }
  }
`;

export const TAG_PRINT_RESTAURANT_VARIANT_PACK_OVERRIDES_QUERY = gql`
  query TagPrint_RestaurantVariantPackOverrides(
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
    $variantPackUUID: uuid!
  ) {
    visible_variant_packs(
      where: {
        variant_pack_uuid: { _eq: $variantPackUUID }
        restaurant_uuid: { _eq: $restaurantUUID }
        supplier_uuid: { _eq: $supplierUUID }
      }
    ) {
      variant_pack {
        ...TagPrintBatchVariantPackFields
      }
    }
  }

  ${TAG_PRINT_RESTAURANT_OVERRIDES_FRAGMENT}
`;

export const DELETE_TAG_PRINT_BATCH_MUTATION = gql`
  mutation TagPrint_BatchDelete(
    $printTagBatchUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    deletePrintTagBatchesForRestaurant(
      print_tag_batch_ids: [$printTagBatchUUID]
      supplier_id: $supplierUUID
      restaurant_id: $restaurantUUID
    ) {
      success
    }
  }
`;

export const REQUEST_PRINT_TAG_BATCH_DELIVERY_MUTATION = gql`
  mutation TagPrint_BatchRequestDelivery(
    $printTagBatchUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    requestPrintTagBatchDelivery(
      print_tag_batch_id: $printTagBatchUUID
      supplier_id: $supplierUUID
      restaurant_id: $restaurantUUID
    ) {
      print_tag_batch {
        ...PrintTagBatchListFields
      }
    }
  }

  ${TAG_PRINT_BATCH_LIST_FIELDS_FRAGMENT}
`;

export const PLANOGRAMS_QUERY = gql`
  fragment PlanogramFields on planograms {
    created_at
    name
    planogram_variant_packs_aggregate(
      where: {
        variant_pack: {
          visible_variant_pack: {
            supplier_uuid: { _eq: $supplierUUID }
            restaurant_uuid: { _eq: $restaurantUUID }
          }
        }
      }
    ) {
      aggregate {
        sum {
          quantity
        }
      }
    }
    uuid
  }

  query Planograms_List($restaurantUUID: uuid!, $supplierUUID: uuid!) {
    getRestaurantPlanograms(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      planogram {
        ...PlanogramFields
      }
    }
  }
`;

export const UPSERT_TAG_PRINT_BATCH_MUTATION = gql`
  mutation TagPrint_UpsertBatch(
    $items: [PrintTagBatchVariantPackInput!]
    $locale: String!
    $name: String
    $printTagBatchUUID: uuid
    $printTagTemplateUUID: uuid!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
  ) {
    upsertPrintTagBatchForRestaurant(
      items: $items
      name: $name
      print_tag_batch_id: $printTagBatchUUID
      print_tag_template_id: $printTagTemplateUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      print_tag_batch {
        ...PrintTagBatchFullFields
      }
    }
  }

  ${TAG_PRINT_BATCH_FULL_FIELDS_FRAGMENT}
`;

export const TAG_PRINT_OFFLINE_QUERY = gql`
  fragment VariantPackPrices on VariantPackPricesResponse {
    manufacturer_suggested_retail_price
    retail_price
    suggested_retail_price
    variant_pack_id
  }

  query TagPrint_Offline(
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $supplierUUID: uuid!
  ) {
    print_tag_batches(
      order_by: { created_at: desc }
      where: {
        print_tag_template: { supplier_uuid: { _eq: $supplierUUID } }
        restaurant_uuid: { _eq: $restaurantUUID }
      }
    ) {
      print_tag_template {
        ...PrintTagTemplateFields
      }
      ...PrintTagBatchFullFields
    }
    print_tag_template_assignments(
      where: {
        _or: [
          { restaurant_uuid: { _eq: $restaurantUUID } }
          { restaurant_uuid: { _is_null: true } }
        ]
        supplier_uuid: { _eq: $supplierUUID }
      }
    ) {
      print_tag_template {
        ...PrintTagTemplateDetailFields
      }
      uuid
    }
    getVisibleVariantPackPrintingPrices(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      variant_pack_prices {
        ...VariantPackPrices
      }
    }
  }

  ${TAG_PRINT_TEMPLATE_DETAIL_FRAGMENT}
  ${TAG_PRINT_BATCH_FULL_FIELDS_FRAGMENT}
  ${PRINT_TAG_TEMPLATE_FIELDS}
`;

export const RENDER_PRINT_ORDER_TEMPLATE_QUERY = gql`
  query Order_PrintOrderTemplate(
    $orderUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    renderPrintOrderTemplate(
      order_id: $orderUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      rendered_template
    }
  }
`;
